'use client'

/**
 * Anonymous funnel beacon (pairs with backend app/api/v1/beacon.py).
 *
 * Fires a fire-and-forget POST to /api/v1/public/beacon on first mount and on
 * every real route change. The backend persists only the top-of-funnel paths
 * (/pricing, /signup), an opaque rotating session id it sets itself via an
 * HttpOnly ps_anon cookie, the referrer, and first-touch UTM params. No PII.
 *
 * We deliberately do NOT read ps_anon here: it is HttpOnly, so JS cannot see
 * it. The browser round-trips it automatically on same-origin requests
 * (credentials: 'include'), and the backend mints a fresh one when absent. We
 * just need to fire the beacon.
 *
 * SSR-safe and non-blocking: the network call lives inside a useEffect (client
 * only), uses keepalive so it survives the navigation that triggered it, and
 * swallows every error so a tracking hiccup never disrupts the visitor. We
 * also record the GA4 page_view funnel event (guarded for gtag undefined).
 */

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

import { getMarketingParams, storeMarketingParams } from '@/lib/marketingParams'
import { pageViewRecorded } from '@/lib/analytics'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.pulsesignal.co'
const BEACON_URL = `${API_URL}/api/v1/public/beacon`

export default function PageBeacon() {
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Persist first-touch attribution as soon as we have a URL to read.
    storeMarketingParams()

    const { utm_source, utm_medium, utm_campaign, signup_referrer } =
      getMarketingParams()

    const payload: Record<string, string> = { path: pathname }
    const referrer = signup_referrer || (document.referrer || '')
    if (referrer) payload.referrer = referrer
    if (utm_source) payload.utm_source = utm_source
    if (utm_medium) payload.utm_medium = utm_medium
    if (utm_campaign) payload.utm_campaign = utm_campaign

    try {
      // keepalive lets the request complete even as the page navigates away.
      // credentials: 'include' round-trips the HttpOnly ps_anon cookie so the
      // backend ties this hit to the visitor's session.
      void fetch(BEACON_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
        keepalive: true,
      }).catch(() => {
        // Best-effort: a beacon failure must never surface to the visitor.
      })
    } catch {
      // fetch unavailable / blocked. No-op.
    }

    // Mirror the funnel step into GA4 (no-op until gtag + consent are live).
    pageViewRecorded(pathname, utm_source)
  }, [pathname])

  return null
}
